import { useIsLoggedIn } from "@/store/selectors";
import {
  usePercentWatchedOfVideo,
  useSecondsWatchedOfVideo,
  useSpecificVideoReaction,
} from "@/store/selectors/userProfileSelectors";
import { Box, Button, Group } from "@mantine/core";
import {
  IconHeart,
  IconHeartFilled,
  IconThumbDown,
  IconThumbDownFilled,
  IconThumbUp,
  IconThumbUpFilled,
} from "@tabler/icons-react";
import { useCallback, useState } from "react";
import { Icon as TablerIcon } from "@tabler/icons-react";
import { reactionTypeToIcon, reactionTypeToColor } from "@/configs/reactions";
import { useStyles } from "@/styles/useStyles";
import { Navigation } from "@/services/Navigation";
import { Notifications } from "@/services/Notifications";
import { useIsPartnerApp } from "@/store/selectors/featureSelectors";

export function VideoFeedbackButtons(props: {
  vimeoId: string;
  onReaction: (reaction: "positive" | "neutral" | "negative") => boolean;
}) {
  const isLoggedIn = useIsLoggedIn();
  const isPartnerApp = useIsPartnerApp();
  const { vimeoId, onReaction } = props;
  const specificVideoReaction = useSpecificVideoReaction({
    vimeoId: vimeoId || "",
  });
  const secondsWatchedOfVideo = useSecondsWatchedOfVideo({
    vimeoId: vimeoId || "",
  });
  const percentWatchedOfVideo = usePercentWatchedOfVideo({
    vimeoId: vimeoId || "",
  });

  const handleFeedbackClick = useCallback(
    (reaction: "positive" | "neutral" | "negative") => {
      if (!isLoggedIn) {
        Navigation.navToLogin();
        Notifications.showNotification({
          title: "Sign in to react to videos",
          type: "warning",
        });
        return false;
      }

      if (secondsWatchedOfVideo < 10 && percentWatchedOfVideo < 50) {
        Notifications.showNotification({
          title: "Watch at least 10 seconds of a video to react to it!",
          type: "warning",
        });
        return false;
      }

      return onReaction(reaction);
    },
    [isLoggedIn, percentWatchedOfVideo, onReaction, secondsWatchedOfVideo],
  );

  if (isPartnerApp) return null;

  return (
    <>
      <style>
        {`
          @keyframes floatUpReaction {
            0% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
            50% {
              opacity: 0.8;
              transform: translateY(-20px) scale(1.4);
            }
            100% {
              opacity: 0;
              transform: translateY(-40px) scale(0.8);
            }
          }
        `}
      </style>
      <Group w="100%" justify="center" align="center" gap="sm" px="lg">
        <FeedbackButton
          reactionType="positive"
          isSelected={specificVideoReaction?.reaction === "positive"}
          onClick={handleFeedbackClick}
          label="This fits me!"
          Icon={IconHeart}
          FilledIcon={IconHeartFilled}
        />
        <FeedbackButton
          reactionType="neutral"
          isSelected={specificVideoReaction?.reaction === "neutral"}
          onClick={handleFeedbackClick}
          label="Interesting"
          Icon={IconThumbUp}
          FilledIcon={IconThumbUpFilled}
        />
        <FeedbackButton
          reactionType="negative"
          isSelected={specificVideoReaction?.reaction === "negative"}
          onClick={handleFeedbackClick}
          label="Not for me"
          Icon={IconThumbDown}
          FilledIcon={IconThumbDownFilled}
        />
      </Group>
    </>
  );
}

function FeedbackButton({
  reactionType,
  isSelected,
  onClick,
  label,
  Icon,
  FilledIcon,
}: {
  reactionType: "positive" | "neutral" | "negative";
  isSelected: boolean;
  onClick: (reaction: "positive" | "neutral" | "negative") => boolean;
  label: string;
  Icon: TablerIcon;
  FilledIcon: TablerIcon;
}) {
  const { theme } = useStyles();
  const [floatingIcons, setFloatingIcons] = useState<number[]>([]);
  const FloatingIcon = reactionTypeToIcon[reactionType];
  const color = reactionTypeToColor[reactionType];

  const handleClick = useCallback(() => {
    const success = onClick(reactionType);
    if (success) {
      const id = Date.now();
      setFloatingIcons((prev) => [...prev, id]);
      setTimeout(() => {
        setFloatingIcons((prev) => prev.filter((i) => i !== id));
      }, 500);
    }
  }, [onClick, reactionType]);

  return (
    <Box pos="relative" flex={1} display="flex">
      <Button
        size="xs"
        color={isSelected ? reactionTypeToColor[reactionType] : "primary.8"}
        radius="xl"
        variant="filled"
        flex={1}
        leftSection={isSelected ? <FilledIcon size={16} /> : <Icon size={16} />}
        onClick={handleClick}
      >
        {label}
      </Button>
      {floatingIcons.map((id) => (
        <Box
          key={id}
          pos="absolute"
          top={4}
          left={12}
          style={{
            animation: "floatUpReaction 500ms ease-out forwards",
            pointerEvents: "none",
            display: "flex",
            zIndex: 1000,
          }}
        >
          <FloatingIcon size={16} color={theme.colors[color][6]} />
        </Box>
      ))}
    </Box>
  );
}
